<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        class="modalOverlay"
        @click.self="() => modal.allowClickAway.value && closeModalContainer()"
        v-if="modal.isOpen.value"
        @keydown="handleKeydown"
      >
        <div
          class="modalContent"
          role="dialog"
          aria-modal="true"
          ref="modalContentRef"
          tabindex="-1"
        >
          <Button
            :class="$style.closeButton"
            :leftIconName="iconNames.close"
            :variant="ButtonVariant.Ghost"
            :size="ButtonSize.Content"
            aria-label="Close"
            @click="closeModalContainer"
          />
          <component
            :key="componentKey"
            v-if="modal.isOpen.value && modal.component.value"
            :is="modal.component.value"
            v-bind="modal.modalProps.value"
            @close="modal.close"
            @hide="modal.close"
            @cancel="modal.cancel"
            @confirm="modal.confirm"
          />
          <div v-else>
            <p class="type-body-medium">No content provided</p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { useGlobalModal } from '@/composables/useGlobalModal'
  import { ButtonVariant, ButtonSize } from '~/constants/buttonTypes'
  import { iconNames } from '~/constants/iconTypes'
  import { ulid } from 'ulid'

  const componentKey = ref(ulid())
  const modalContentRef = ref<HTMLElement | null>(null)

  const closeModalContainer = () => {
    modal.close()
  }

  const modal = useGlobalModal()

  const FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ')

  function getFocusableElements(): HTMLElement[] {
    if (!modalContentRef.value) return []
    return Array.from(
      modalContentRef.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
    )
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      closeModalContainer()
      return
    }

    if (e.key === 'Tab') {
      const focusable = getFocusableElements()
      if (focusable.length === 0) {
        e.preventDefault()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
  }

  watch(
    () => modal.isOpen.value,
    (isOpen) => {
      if (isOpen) {
        document.body.style.overflow = 'hidden'
        nextTick(() => {
          const focusable = getFocusableElements()
          if (focusable.length > 0) {
            focusable[0].focus()
          } else {
            modalContentRef.value?.focus()
          }
        })
      } else {
        document.body.style.overflow = ''
      }
    },
  )

  onMounted(() => {
    componentKey.value = ulid()
  })

  onUnmounted(() => {
    document.body.style.overflow = ''
  })
</script>
<style module>
  .closeButton {
    position: absolute;
    top: var(--space-24);
    right: var(--space-8);
  }
</style>
<style scoped>
  .modalOverlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: color-mix(
      in srgb,
      var(--color-text-primary) 50%,
      transparent
    );
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1001;
  }

  .modalContent {
    position: relative;
    background-color: var(--color-surface-default);
    border-radius: var(--radius-lg);
    padding: var(--space-48);
    box-shadow: var(--elevation-md);
    max-width: 90%;
    max-height: 90%;
    overflow: auto;
    outline: none;
  }

  .modal-fade-enter-active,
  .modal-fade-leave-active {
    transition: opacity 0.3s;
  }

  .modal-fade-enter-from,
  .modal-fade-leave-to {
    opacity: 0;
  }
</style>
